import { useCallback, useEffect, useMemo } from 'react';
import { useOrgStore } from '@Models/useOrgModel';
import { QuarantineProvider, useQuarantineStore } from '@Quarantine/store';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertCloseButton,
  Box,
  Collapse,
  Spinner,
  Flex,
  Stack,
  Button,
  Icon,
  Text,
  useColorMode,
  useColorStyle,
  Divider,
  Space,
  Skeleton,
} from '@tonic-one/react';
import {
  ChevronLeftIcon,
  EmailOIcon,
  WarningMinorIcon,
} from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import { dayjs } from '@Utils/time';
import EnableEmailPreviewModal from '@Quarantine/EnableEmailPreviewModal';
import { useDisclosure } from 'src/components/Common/Tooltip/TextEllipsis/utils';
import { useToggle } from '@tonic-one/react-hooks';
import { QuarantineApi } from '@Apis';
import { RiskAiRiskType } from '@Quarantine/RiskAI/types';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { LogTimeUnit } from '@Log/shared';
import { handleNavigate } from '@Utils/common';
import { OperationTabUrl } from '@Quarantine/utils/constants';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'src/QueryClient';
import { useRiskAIExpressionsQuery } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/query';
import { getLogServiceNameFromScanSource } from '../utils';

const DetectionDetails = () => {
  const navigate = useNavigate();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { orgId } = useOrgStore();
  const { offset } = useUtcOffsetStore();
  const { fetchDetectionById, fetchDetectionBlobPath } = useQuarantineStore(
    (reducerState) => reducerState.riskAi
  );
  const location = useLocation();
  const { state: locationState } = location;

  const detectionId = location.pathname.split('/').pop()?.split('?')?.[0];
  const [isAlertOpen, onAlertClose] = useToggle(true);

  const { data: detection, isFetching: detectionFetching } = useQuery({
    enabled: !!detectionId,
    queryKey: ['detection', detectionId],
    queryFn: async () => {
      if (!detectionId) return;
      const timeRange = {
        predefined: LogTimeUnit.All,
        timezoneOffset: offset,
        start: dayjs().subtract(180, 'days').toISOString(),
        end: dayjs().toISOString(),
      };
      const { data } = await fetchDetectionById({
        messageId: detectionId,
        timeRange,
      });
      return data;
    },
  });

  const isQuarantineRelatedAction = useMemo(
    () => detection?.action?.toLowerCase().includes('quarantine'),
    [detection]
  );

  const isDeleted = useMemo(
    () => isDeleteAction(detection?.action),
    [detection]
  );

  const isAnomaly = useMemo(() => {
    if (!detection) return false;
    return [RiskAiRiskType.Anomaly, RiskAiRiskType.CustomizedAnomaly].includes(
      detection.raiRiskType
    );
  }, [detection]);

  const navigateBack = useCallback(() => {
    let url: string = OperationTabUrl.RiskAi;
    if (locationState?.from) {
      url = locationState.from;
    } else if (detection) {
      // add riskType=raiRiskType to url as query param
      url = `${OperationTabUrl.RiskAi}?riskType=${detection.raiRiskType}`;
    }
    handleNavigate(url);
    navigate(url);
  }, [detection, locationState, navigate]);

  const {
    data: mailPreviewEnabledList,
    isFetching: mailPreviewEnabledFetching,
  } = useQuery({
    enabled: !!detection && !isDeleted && isQuarantineRelatedAction,
    queryKey: ['mailPreviewEnabledList'],
    queryFn: async () => {
      const { data } = await QuarantineApi.apiGetMailPreviewEnabledList();
      return data;
    },
  });

  const isEmailPreviewEnabled = useMemo(() => {
    if (!mailPreviewEnabledList) return false;
    if (!Array.isArray(mailPreviewEnabledList)) {
      return false;
    }
    return orgId === 'all'
      ? mailPreviewEnabledList.some((item) => item.enabled)
      : mailPreviewEnabledList.some(
          (item) => item.orgId === orgId && item.enabled
        );
  }, [mailPreviewEnabledList, orgId]);

  const { data: digestBlobPath, isFetching: digestBlobPathFetching } = useQuery(
    {
      enabled:
        !isDeleted &&
        isEmailPreviewEnabled &&
        !!detection?.internetMessageId &&
        isQuarantineRelatedAction,
      queryKey: ['digestBlobPath', detectionId],
      queryFn: async () => {
        if (!detection?.internetMessageId) return;
        const timeRange = {
          predefined: LogTimeUnit.All,
          timezoneOffset: offset,
          start: dayjs().subtract(180, 'days').toISOString(),
          end: dayjs().toISOString(),
        };
        const res = await fetchDetectionBlobPath({
          serviceName: getLogServiceNameFromScanSource(detection.scanSource),
          messageId: detection.internetMessageId,
          timeRange,
        });
        return res.data;
      },
    }
  );

  const { data: mailPreviewContent, isFetching: mailPreviewFetching } =
    useQuery({
      enabled: !!digestBlobPath,
      queryKey: ['mailPreviewContent', detectionId],
      queryFn: async () => {
        if (!digestBlobPath || !detection) return;
        return (await QuarantineApi.apiGetMailPreviewContent(
          digestBlobPath,
          detection.traceId
        )) as unknown as string | undefined;
      },
    });

  const { data: expressions, isPending: expressionsPending } =
    useRiskAIExpressionsQuery();

  const isFetching = useMemo(() => {
    return (
      detectionFetching ||
      mailPreviewEnabledFetching ||
      digestBlobPathFetching ||
      mailPreviewFetching
    );
  }, [
    detectionFetching,
    digestBlobPathFetching,
    mailPreviewEnabledFetching,
    mailPreviewFetching,
  ]);

  useEffect(() => {
    if (!detectionId) {
      navigateBack(); // no detectionId
      return;
    }
  }, [detectionId, navigateBack]);

  const renderEnablePreviewAlert = () => (
    <Collapse in={isAlertOpen} unmountOnExit>
      <Alert
        variant="outline"
        severity="info"
        height="10x"
        mt="4x"
        onClose={onAlertClose}
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontWeight={500}>
            {t('riskAi.detectionAnalysis.preview.disabled')}
          </Text>
          <Flex columnGap="4x" alignItems="center">
            <Button mt="-2px" size="sm" variant="secondary" onClick={onOpen}>
              {t('riskAi.detectionAnalysis.preview.enable')}
            </Button>
            <AlertCloseButton width="max-content" height="max-content" />
          </Flex>
        </Flex>
      </Alert>
    </Collapse>
  );

  const renderStatusDeletedAlert = () => (
    <Collapse in={isAlertOpen} unmountOnExit>
      <Alert
        variant="outline"
        severity="info"
        height="10x"
        mt="4x"
        onClose={onAlertClose}
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontWeight={500}>
            {t('riskAi.detectionAnalysis.preview.notSupported')}
          </Text>
          <Flex columnGap="4x" alignItems="center">
            <AlertCloseButton width="max-content" height="max-content" />
          </Flex>
        </Flex>
      </Alert>
    </Collapse>
  );

  const renderMailPreview = () => {
    if (!isQuarantineRelatedAction) {
      return null;
    }

    if (isDeleted) {
      return renderStatusDeletedAlert();
    }

    if (isFetching) {
      return null;
    }

    if (!isEmailPreviewEnabled) {
      return renderEnablePreviewAlert();
    }

    if (mailPreviewContent) {
      return (
        <Box mt="4x" px="6x" wordBreak="break-all">
          {mailPreviewContent}
        </Box>
      );
    }

    return null;
  };

  const getRiskTypeColor = (riskType: string) => {
    const colorMap = {
      [RiskAiRiskType.SecurityRisk.toLowerCase()]: 'red:50',
      [RiskAiRiskType.Anomaly.toLowerCase()]: 'yellow:50',
      [RiskAiRiskType.CustomizedAnomaly.toLowerCase()]: 'yellow:50',
    };
    return colorMap[riskType.toLowerCase()];
  };

  const renderAgenticAIItems = () => {
    if (!detection?.raiLLMThreatAnalysis?.most_suspicious_items) {
      return <Text>N/A</Text>;
    }

    return detection.raiLLMThreatAnalysis.most_suspicious_items.map(
      (item, index) => (
        <Box key={index}>
          <Flex alignItems="center">
            <Icon as={WarningMinorIcon} size="4x" mr="2x" color="yellow:50" />
            <Text size="md" fontWeight={500}>
              {item}
            </Text>
          </Flex>
        </Box>
      )
    );
  };

  const renderMatchedFilters = () => {
    if (!detection) {
      return null;
    }

    if (expressionsPending) {
      return detection.matchedFilters.map((_, index) => (
        <Stack key={index} gap="1x">
          <Skeleton height="22px" width="25%" animation="wave" />
          <Skeleton height="40px" width="50%" animation="wave" />
        </Stack>
      ));
    }

    if (!expressions) {
      return null;
    }

    return detection.matchedFilters.map((filterName, index) => {
      const expression = expressions.find(
        (exp) => exp.raiExpName === filterName
      );
      return (
        <Box key={index}>
          <Flex alignItems="center">
            <Icon as={WarningMinorIcon} size="4x" mr="2x" color="yellow:50" />
            <Text size="md" fontWeight={500}>
              {expression?.raiExpName || filterName}
            </Text>
          </Flex>
          <Flex mt="1x">
            <Space width="6x" />
            <Text maxWidth={1000} color={colorStyle.color.secondary}>
              {expression?.raiExpDesc || ''}
            </Text>
          </Flex>
        </Box>
      );
    });
  };

  return (
    <Flex direction="column" py="3x" minHeight="inherit">
      <Box>
        <Button
          variant="ghost"
          columnGap="1x"
          mb="4x"
          onClick={navigateBack}
          color={colorStyle.color.secondary}
          border="none"
        >
          <Icon as={ChevronLeftIcon} height="4x" fontWeight="bold" />
          <Text size="md">{t('common.action.label.back')}</Text>
        </Button>
        <Text size="xl" px="4x" mb="4x">
          {t('riskAi.detectionAnalysis.title')}
        </Text>
      </Box>
      {!detection ? (
        <Flex flex="1 1 auto">
          <Spinner m="auto" />
        </Flex>
      ) : (
        <Box>
          <Stack
            flex="1 1 auto"
            p="6x"
            mx="4x"
            backgroundColor={colorStyle.background.secondary}
          >
            <Box mb="4x">
              <Flex alignItems="center" gap="2x">
                <Icon as={EmailOIcon} size="4x" />
                <Text size="xl" fontWeight={500}>
                  {detection.msgSubject}
                </Text>
              </Flex>
              <Text mt="1x" pl="6x" color={colorStyle.color.secondary}>
                {dayjs(detection.timestamp)
                  .utc(true)
                  .format('YYYY/MM/DD HH:mm')}
              </Text>
            </Box>
            <Stack gap="1x" pl="6x">
              <Flex>
                <Text color={colorStyle.color.secondary}>
                  {t('riskAi.detectionAnalysis.email.sender')}:
                </Text>
                <Text ml="2x">{detection.msgFrom}</Text>
              </Flex>
              <Flex>
                <Text color={colorStyle.color.secondary}>
                  {t('riskAi.detectionAnalysis.email.recipient')}:
                </Text>
                <Text ml="2x">{detection.mailbox}</Text>
              </Flex>
            </Stack>
            <Divider mt="4x" variant="dashed" />
            {isFetching && !isDeleted && (
              <Skeleton height="10x" width="100%" mt="4x" animation="wave" />
            )}
            {renderMailPreview()}
          </Stack>
          <Box p="6x" m="4x" backgroundColor={colorStyle.background.secondary}>
            <Flex>
              <Stack gap="2x" pr="6x">
                <Text color={colorStyle.color.secondary}>
                  {isAnomaly
                    ? t('riskAi.detectionAnalysis.anomalyName')
                    : t('riskAi.detectionAnalysis.securityRiskName')}
                </Text>
                <Text fontWeight={400} size="lg">
                  {detection.securityRiskName}
                </Text>
              </Stack>
              <Divider mx="4x" orientation="vertical" />
              <Stack gap="2x" pr="6x">
                <Text color={colorStyle.color.secondary}>
                  {t('riskAi.detectionAnalysis.threatType')}
                </Text>
                <Text fontWeight={400} size="lg">
                  {detection.threatType}
                </Text>
              </Stack>
              <Divider mx="4x" orientation="vertical" />
              <Stack gap="2x" pr="6x">
                <Text color={colorStyle.color.secondary}>
                  {t('riskAi.detectionAnalysis.action')}
                </Text>
                <Text fontWeight={400} size="lg" mt="auto">
                  {t(`quarantine.action.${detection.action}`)}
                </Text>
              </Stack>
              <Divider mx="4x" orientation="vertical" />
              <Stack gap="2x" pr="6x">
                <Text color={colorStyle.color.secondary}>
                  {t('riskAi.detectionAnalysis.riskType')}
                </Text>
                <Text
                  fontWeight={400}
                  size="lg"
                  mt="auto"
                  color={getRiskTypeColor(detection.raiRiskType)}
                >
                  {t(
                    `riskAi.detectionAnalysis.riskType.${detection.raiRiskType.toLowerCase()}`,
                    detection.raiRiskType
                  )}
                </Text>
              </Stack>
            </Flex>
            <Divider my="4x" />
            <Stack gap="4x">
              {detection.detectedBy === t('riskAi.llm') &&
              detection.raiLLMThreatAnalysis ? (
                <>
                  <Stack gap="2x">
                    <Text color={colorStyle.color.secondary}>
                      {t('riskAi.agenticAi.summary')}
                    </Text>
                    <Text>
                      {detection.raiLLMThreatAnalysis?.summary || 'N/A'}
                    </Text>
                  </Stack>
                  <Divider />
                  <Stack gap="2x">
                    <Text color={colorStyle.color.secondary}>
                      {t('riskAi.agenticAi.topIndicators')}
                    </Text>
                    {renderAgenticAIItems()}
                  </Stack>
                </>
              ) : (
                renderMatchedFilters()
              )}
            </Stack>
          </Box>
        </Box>
      )}
      <EnableEmailPreviewModal
        isOpen={isOpen}
        onClose={onClose}
        onUpdate={(updatedList) => {
          queryClient.setQueryData(
            ['mailPreviewEnabledList'],
            () => updatedList
          );
        }}
      />
    </Flex>
  );
};

const DetectionDetailsWithStore = () => {
  const { orgId } = useOrgStore();
  const { offset } = useUtcOffsetStore();
  return (
    <QuarantineProvider orgId={orgId} utcOffset={offset}>
      <DetectionDetails />
    </QuarantineProvider>
  );
};

export default DetectionDetailsWithStore;

function isDeleteAction(action?: string) {
  return !!action?.toLowerCase().includes('delete');
}
